import { Fragment, useEffect } from "react";
import { Card, Employee, IconNames, Shop } from "@/types/v2";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/Base/DropdownMenu";
import { DropdownMenuLabel } from "@radix-ui/react-dropdown-menu";
import { CardMetrics } from "@/components/Dashboard/components/Card/Card.Metrics";
import { CardActionDialogs } from "@/components/Dashboard/components/Card/CardActionDialogs";
import { useStore } from "@/stores/useStore";
import { Icon } from "@/components/Common/Icon";
import { FeatureFlagged } from "@/components/Common/FeatureFlagged";
import { useCustomerUpdates } from "@/hooks/useCustomerUpdates";
import { useCardMenuActions } from "@/hooks/useCardMenuActions";
import { getDefaultForceEffectColor } from "@/lib/cards";
import { cn } from "@/lib/utils";
import { ClassNameValue } from "tailwind-merge";

export interface CardMenuProps {
  cardData: Card;
  disabled?: boolean;
  employees?: Employee[];
  shop: Shop;
  triggerClassName?: ClassNameValue;
  triggerIconSize?: number;
  onMoveToClick?: (card: Card) => void;
  /**
   * Notifies the parent when the menu opens/closes so the card can pause any
   * transform animation (e.g. wiggle) while the dropdown is open — otherwise
   * the moving trigger drags the open menu around with it.
   */
  onMenuOpenChange?: (open: boolean) => void;
}

export const CardMenu = ({
  cardData,
  disabled,
  employees,
  shop,
  triggerClassName,
  triggerIconSize = 14,
  onMoveToClick,
  onMenuOpenChange,
}: CardMenuProps) => {
  const { tags } = useStore();
  const { isCustomerUpdatesEnabled } = useCustomerUpdates();
  const {
    open,
    setOpen,
    dialogOpen,
    setDialogOpen,
    dialog,
    isLabelShownAsTag,
    duplicatePrefill,
    handleRemoveCard,
    handleMarkComplete,
    handleEditCard,
    handleEditTag,
    handleToggleLabelAsTag,
    handleResetCard,
    handleForceEffect,
    handleDuplicateCard,
    handleOpenDialog,
    handleViewDetails,
    handleMoveToClick,
  } = useCardMenuActions(cardData, shop, tags, onMoveToClick);

  // `open` can close via paths other than Radix's own onOpenChange -- every
  // menu item that opens a dialog (handleOpenDialog) flips `open` to false
  // directly, which Radix's controlled DropdownMenu does not surface through
  // onOpenChange (that only fires for its own internally-detected triggers).
  // Track `open` itself so the parent learns about every close, not just
  // Radix-initiated ones (see WEBV3-309: this desync silently disabled the
  // wiggle animation, which gates on `!menuOpen`).
  useEffect(() => {
    onMenuOpenChange?.(open);
  }, [open, onMenuOpenChange]);

  return (
    <Fragment>
      <CardActionDialogs
        cardData={cardData}
        shop={shop}
        dialogOpen={dialogOpen}
        dialog={dialog}
        setDialogOpen={setDialogOpen}
        handleEditCard={handleEditCard}
        handleEditTag={handleEditTag}
        handleRemoveCard={handleRemoveCard}
        handleMarkComplete={handleMarkComplete}
        handleForceEffect={handleForceEffect}
        duplicatePrefill={duplicatePrefill}
        handleDuplicateCard={handleDuplicateCard}
      />
      <CardMetrics
        cardData={cardData}
        externalTrigger
        open={dialogOpen && dialog === "metrics"}
        onOpenChange={setDialogOpen}
        employees={employees}
        shop={shop}
      />
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger
          disabled={disabled}
          className={cn(
            "z-5 cursor-pointer rounded-sm p-1 text-accent hover:bg-white/50 disabled:cursor-default disabled:hover:bg-transparent",
            triggerClassName
          )}
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Icon name={"moreVertical"} size={triggerIconSize} />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          onMouseDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className="flex flex-col gap-1 p-1"
        >
          <DropdownMenuLabel className="px-2">Card Menu</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleViewDetails}>
            <Icon name={IconNames.open} size={14} />
            <span>View Details</span>
          </DropdownMenuItem>
          {onMoveToClick && (
            <DropdownMenuItem onClick={handleMoveToClick}>
              <Icon name={IconNames.move} size={14} />
              <span>Move To</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleOpenDialog("markComplete");
            }}
          >
            <Icon name={IconNames.check} size={14} />
            <span>Mark as Complete</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {cardData.source === "manual" ? (
            <>
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleOpenDialog("form");
                }}
              >
                <Icon name={"edit"} size={14} />
                <span>Edit</span>
              </DropdownMenuItem>
            </>
          ) : (
            <>
              {cardData.label && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleToggleLabelAsTag();
                  }}
                >
                  <Icon name={"tag"} size={14} />
                  <span>{isLabelShownAsTag ? "Hide Label" : "Show Label"}</span>
                </DropdownMenuItem>
              )}
            </>
          )}
          <DropdownMenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleOpenDialog("tags");
            }}
          >
            <Icon name={"tags"} size={14} />
            <span>Tags</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleOpenDialog("duplicate");
            }}
          >
            <Icon name={IconNames.duplicate} size={14} />
            <span>Duplicate Card</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleOpenDialog("remove");
            }}
          >
            <Icon name={"delete"} size={14} />
            <span>Remove</span>
          </DropdownMenuItem>
          {cardData.source === "manual" &&
            (!!cardData.automationAction || !!cardData.manualAction) && (
              <DropdownMenuSeparator />
            )}
          {(!!cardData.automationAction || !!cardData.manualAction) && (
            <Fragment>
              <DropdownMenuItem onClick={handleResetCard}>
                <Icon name={"undo"} size={14} />
                <span>Reset Effects</span>
              </DropdownMenuItem>
            </Fragment>
          )}
          <FeatureFlagged feature={"isForceCardEffectsEnabled"}>
            <DropdownMenuItem
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleOpenDialog("flashColor");
              }}
            >
              <Icon name={IconNames.flashColor} size={14} />
              <span>Flash Card</span>
            </DropdownMenuItem>
          </FeatureFlagged>
          <FeatureFlagged feature={"isForceCardEffectsEnabled"}>
            <DropdownMenuItem
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleForceEffect("wiggle", getDefaultForceEffectColor(shop));
              }}
            >
              <Icon name={IconNames.wiggleCard} size={14} />
              <span>Wiggle Card</span>
            </DropdownMenuItem>
          </FeatureFlagged>
          <FeatureFlagged
            feature={"isCustomerUpdatesEnabled"}
            enabled={isCustomerUpdatesEnabled}
          >
            <DropdownMenuSeparator />
          </FeatureFlagged>
          <FeatureFlagged
            feature={"isCustomerUpdatesEnabled"}
            enabled={isCustomerUpdatesEnabled}
          >
            <DropdownMenuItem
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleOpenDialog("customerUpdate");
              }}
            >
              <Icon name={"bell"} size={14} />
              <span>Customer Update</span>
            </DropdownMenuItem>
          </FeatureFlagged>
          <FeatureFlagged
            feature={"isCustomerUpdatesEnabled"}
            enabled={isCustomerUpdatesEnabled}
          >
            <DropdownMenuItem
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleOpenDialog("customerUpdatesTimeline");
              }}
            >
              <Icon name={IconNames.clock} size={14} />
              <span>Customer Updates Timeline</span>
            </DropdownMenuItem>
          </FeatureFlagged>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleOpenDialog("metrics");
            }}
          >
            <Icon name={"metrics"} size={14} />
            <span>Metrics</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </Fragment>
  );
};
